import { useMemo, useState } from "react";
import { INIT_STYLE_VALUES } from "../../constants";
import { scaleOrdinal, scaleSequential } from "d3-scale";
import { interpolateRainbow } from "d3-scale-chromatic";

const colors = (specifier) => {
  var n = (specifier.length / 6) | 0,
    colors = new Array(n),
    i = 0;
  while (i < n) colors[i] = "#" + specifier.slice(i * 6, ++i * 6);
  return colors;
};

const colorsString20 =
  "1F77B4AEC7E8FF7F0EFFBB782CA02C98DF8AD62728FF98969467BDC5B0D58C564BC49C94E377C2F7B6D27F7F7FC7C7C7BCBD22DBDB8D17BECF9EDAE5";

const buildScale = (values) => {
  const scale =
    values.length <= 20
      ? scaleOrdinal(colors(colorsString20))
      : scaleSequential(interpolateRainbow);

  const degree = 100 / values.length / 100;

  return values.reduce((acc, val, i) => {
    acc.push([val]);

    if (values.length <= 20) {
      acc.push(scale(val));
    } else {
      acc.push(scale(i * degree));
    }

    return acc;
  }, []);
};

/**
 * Builds the circle paint for the WQ Data style
 * Each location is colored by its worst (max) benchmark scale value
 * across the selected parameters
 * Returns null until the data required to build it is available
 */
const buildWqPaint = ({
  benchmarkData,
  benchmarkScaleColors,
  parameters,
  flow,
  analysis,
}) => {
  // the placeholder colors (used before the API responds) have no bmk_value
  const hasScaleColors = benchmarkScaleColors?.some(
    (color) => color.bmk_value !== undefined
  );
  if (!benchmarkData?.length || !hasScaleColors || !flow) {
    return null;
  }

  const benchmarkField = `${flow}_bmk_${
    analysis === "median" ? "median" : "pctile"
  }`;

  const benchmarksByScaleValue = benchmarkScaleColors.reduce((acc, curr) => {
    acc[curr.bmk_value] = curr.dot_symbol_color;
    return acc;
  }, {});

  const locationsWithMaxBenchmark = new Map();
  benchmarkData.forEach((d) => {
    if (
      [null, undefined].includes(d[benchmarkField]) ||
      !parameters?.includes(d.parameter_index)
    ) {
      return;
    }
    const existingRecord = locationsWithMaxBenchmark.get(d.location_name);
    if (!existingRecord || existingRecord[benchmarkField] < d[benchmarkField]) {
      locationsWithMaxBenchmark.set(d.location_name, d);
    }
  });

  const noDataColor = benchmarksByScaleValue[-1] ?? "black";

  return {
    "circle-color": locationsWithMaxBenchmark.size
      ? [
          "match",
          ["get", "location_name"],
          ...[...locationsWithMaxBenchmark.values()].flatMap((value) => [
            [value.location_name],
            benchmarksByScaleValue[value[benchmarkField]] ?? noDataColor,
          ]),
          noDataColor,
        ]
      : noDataColor,
    "circle-stroke-width": 2,
    "circle-stroke-color": "black",
  };
};

/**
 * Controls the "Color Data Points by" style
 * The active style is derived from `activeStyleId` and the data it depends
 * on, so it always reflects the current filters without imperative updates
 * Consumers apply `activeStyle.paint` to the map when `isStyleReady`
 */
const useLayerStyles = ({
  analysis,
  flow,
  parameters,
  benchmarkScaleColors,
  benchmarkData,
  isBenchmarkDataLoading = false,
  filtersData,
  initialStyleId = "locationTypes",
}) => {
  const [activeStyleId, setActiveStyleId] = useState(initialStyleId);

  const baseStyleValues = useMemo(() => {
    if (!filtersData) return INIT_STYLE_VALUES;
    return {
      ...INIT_STYLE_VALUES,
      locationTypes: {
        ...INIT_STYLE_VALUES.locationTypes,
        paint: {
          "circle-color": [
            "match",
            ["get", "location_type"],
            ...buildScale(
              filtersData.locationTypes.map((item) => item.display)
            ),
            "#fff",
          ],
          "circle-stroke-width": 2,
          "circle-stroke-color": "black",
        },
      },
      parameterGroups: {
        ...INIT_STYLE_VALUES.parameterGroups,
        paint: {
          "circle-color": [
            "match",
            ["get", "parameter_group_fav"],
            ...buildScale(
              filtersData.parameterGroups.map((item) => item.display)
            ),
            "#fff",
          ],
          "circle-stroke-width": 2,
          "circle-stroke-color": "black",
        },
      },
    };
  }, [filtersData]);

  const wqPaint = useMemo(
    () =>
      buildWqPaint({
        benchmarkData,
        benchmarkScaleColors,
        parameters,
        flow,
        analysis,
      }),
    [benchmarkData, benchmarkScaleColors, parameters, flow, analysis]
  );

  const styleValues = useMemo(
    () => ({
      ...baseStyleValues,
      wqData: {
        ...baseStyleValues.wqData,
        paint: wqPaint || baseStyleValues.wqData.paint,
      },
    }),
    [baseStyleValues, wqPaint]
  );

  const activeStyle = styleValues[activeStyleId] || styleValues.locationTypes;

  // the WQ placeholder paint is only applied once the data has finished
  // loading without results (e.g. the request failed)
  const isStyleReady =
    !!filtersData &&
    (activeStyleId !== "wqData" || !!wqPaint || !isBenchmarkDataLoading);

  const styleOptions = useMemo(
    () =>
      Object.entries(styleValues).map(([key, value]) => ({
        display: value.name,
        value: key,
      })),
    [styleValues]
  );

  return {
    activeStyle,
    activeStyleId,
    handleActiveStyle: setActiveStyleId,
    isStyleReady,
    styleOptions,
    styleValues,
  };
};

export default useLayerStyles;
